import {
  Box,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import Decimal from "decimal.js";
import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import { EquipmentExtType } from "../../contexts/types";
import { formatNumber, formatYearMonthByLocale } from "../../utils/utils";
import { ConditionTable } from "./SimulationNewProduct";

/** 設備入力用ヘッダスタイル */
const equipmentHeaderStyle = {
  fontSize: "16px",
};

/** 設備入力用ボディスタイル */
const equipmentBodyStyle = {
  padding: "7px",
};

/**
 * 新製品製造シミュレーションの「段階的設備投資」セクションを表示するコンポーネントのProps
 */
interface Props {
  /** 設備情報一覧 */
  equipmentAmounts: EquipmentExtType[];
  /** GoogleSpread連携時の無効値エラー情報 */
  pciSyncError: { invalidValueErrors: string[] };
  /** 条件テーブル（工場為替情報等） */
  conditionTable: ConditionTable | undefined;
  /**
   * 設備情報の任意プロパティを変更する
   * @param equipmentId 設備ID
   * @param propertyName 更新対象プロパティ名
   * @param value 更新値
   */
  changeEquipmentAmounts: (equipmentId: string, propertyName: keyof EquipmentExtType, value: any) => void;
}

/**
 * 新製品製造シミュレーションの「段階的設備投資」セクションを表示するコンポーネント
 * SimulationNewProduct.tsx から切り出した対応B（コンポーネント分割 + React.memo化）
 */
const PhasedEquipmentInvestmentSection: React.FC<Props> = memo((props) => {
  const {
    equipmentAmounts,
    pciSyncError,
    conditionTable,
    changeEquipmentAmounts,
  } = props;

  const { t } = useTranslation();

  // console.info(`★レンダリング - 段階的設備投資`); // 再描画確認用

  return (
    <Box mt={6} p={4} border="1px solid #ccc" borderRadius="md" shadow="sm">
      <Text mb={4} fontSize="lg" fontWeight="bold">{t('features.InvestmentScreening.SimulationNewProduct.heading.phasedEquipmentInvestment')}</Text>
      {/* 無効値エラーメッセージ */}
      {pciSyncError.invalidValueErrors.length > 0 && (
        <Box mb={2}>
          {pciSyncError.invalidValueErrors.map((error, index) => (
            <Text key={index} color="orange.500" fontSize="sm">{error}</Text>
          ))}
        </Box>
      )}

      <TableContainer borderTop="1px solid" borderBottom="1px solid" borderColor="gray.200" maxHeight="400px" w="100%" overflowX="auto" overflowY="auto" mt={1}>
        <Table className="mm-ceas-table" cellSpacing={0} width="1325px">
          <colgroup>
            <col width="*" />
            <col width="185px" />
            <col width="120px" />
            <col width="250px" />
            <col width="110px" />
            <col width="110px" />
            <col width="110px" />
            <col width="110px" />
          </colgroup>
          <Thead position="sticky" top={0} bg="white" zIndex="10" >
            <Tr>
              <Th style={equipmentHeaderStyle}>{t('features.InvestmentScreening.SimulationNewProduct.tableHeader.equipmentName')}</Th>
              <Th style={equipmentHeaderStyle}>{t('features.InvestmentScreening.SimulationNewProduct.tableHeader.unitPrice')}</Th>
              <Th style={equipmentHeaderStyle}>{t('features.InvestmentScreening.SimulationNewProduct.tableHeader.quantity')}</Th>
              <Th style={equipmentHeaderStyle}>{t('features.InvestmentScreening.SimulationNewProduct.tableHeader.amount')}</Th>
              <Th style={equipmentHeaderStyle}>{t('features.InvestmentScreening.SimulationNewProduct.tableHeader.acceptanceMonthBefore')}</Th>
              <Th style={equipmentHeaderStyle}>{t('features.InvestmentScreening.SimulationNewProduct.tableHeader.acceptanceMonthAfter')}</Th>
              <Th style={equipmentHeaderStyle}>{t('features.InvestmentScreening.SimulationNewProduct.tableHeader.depreciationStartMonthBefore')}</Th>
              <Th style={equipmentHeaderStyle}>{t('features.InvestmentScreening.SimulationNewProduct.tableHeader.depreciationStartMonthAfter')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {equipmentAmounts.map((e: EquipmentExtType) => (
              <Tr key={e.equipmentId} bgColor={e.selected ? "transparent" : "#eee"} >
                <Td style={equipmentBodyStyle}>
                  <Text width="fit-content">
                    {e.equipmentName}
                  </Text>
                </Td>
                <Td style={equipmentBodyStyle}>
                  <Text width="fit-content" ml="auto">
                    {formatNumber(e.unitPrice!, "WithDecimal", 4)}
                  </Text>
                </Td>
                <Td style={equipmentBodyStyle}>
                  <Text width="fit-content" ml="auto">
                    {formatNumber(e.changeInQuantity, "")}
                  </Text>
                </Td>
                <Td style={equipmentBodyStyle}>
                  <Text width="fit-content" ml="auto">
                    {formatNumber(new Decimal(e.amount || 0), conditionTable?.factoryExchange?.currencyName!)}
                  </Text>
                </Td>
                <Td style={equipmentBodyStyle}>
                  <Text width="fit-content" ml="auto">
                    {formatYearMonthByLocale(e.acceptanceMonth)}
                  </Text>
                </Td>
                <Td style={equipmentBodyStyle}>
                  <Input
                    type="month"
                    style={{ border: '1px solid #ccc' }}
                    value={e.changedAcceptanceMonth ?? e.acceptanceMonth}
                    isDisabled={!e.selected}
                    onChange={(event) => changeEquipmentAmounts(e.equipmentId!, "changedAcceptanceMonth", event.target.value)}
                  />
                </Td>
                <Td style={equipmentBodyStyle}>
                  <Text width="fit-content" ml="auto">
                    {formatYearMonthByLocale(e.depreciationStartMonth)}
                  </Text>
                </Td>
                <Td style={equipmentBodyStyle}>
                  <Input
                    type="month"
                    style={{ border: '1px solid #ccc' }}
                    value={e.changedDepreciationStartMonth ?? e.depreciationStartMonth}
                    isDisabled={!e.selected}
                    onChange={(event) => changeEquipmentAmounts(e.equipmentId!, "changedDepreciationStartMonth", event.target.value)}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
});

PhasedEquipmentInvestmentSection.displayName = "PhasedEquipmentInvestmentSection";

export default PhasedEquipmentInvestmentSection;
